import * as React from 'react'
import { useTheme } from 'react-admin'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Toolbar,
  useTheme as useMuiTheme,
} from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

export const TransparentDialog = ({ open, handleClose, size, minHeight, children, filterTitle, displayDialogActions = true }) => (
  <Dialog
    open={open}
    fullWidth
    maxWidth={size || 'lg'}
    onClose={handleClose}
    SlotProps={{
      input: {
        paper: {
          borderRadius: '10px',
          boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px'
        },
      },
      backdrop: { style: { backgroundColor: 'rgba(255, 255, 255, 0.4)' } }
    }}
  >
    { displayDialogActions && (
      <DialogActions style={{ dislay: 'flex', justifyContent: 'space-between' }}>
        <span style={{ padding: '5px', fontWeight: 'bold', fontSize: '1.2rem', color: '#444' }}>{filterTitle}</span>

        <Button onClick={handleClose} color="primary">
          <HighlightOffIcon style={{ color: '#999', fontSize: '1.2rem' }}/>
        </Button>
      </DialogActions>
    ) }

    <DialogContent style={{ minHeight: minHeight ?? '200px', overflow: 'auto', padding: '20px' }}>
      { children }
    </DialogContent>
  </Dialog>
)

export const CommonDialog = ({ open, handleClose, title, size, minHeight, maxHeight, children, displayDialogActions = true, titleBgColor, titleColor, widthInPx = false, icon = null, closeLabel = 'Fermer', sx: externalSx, compact = false }) => {
  const [theme] = useTheme()
  const muiTheme = useMuiTheme()

  if ( ! titleBgColor ) titleBgColor = theme === 'dark' ? muiTheme.palette.primary.main : '#c5dedd'
  if ( ! titleColor ) titleColor = theme === 'dark' ? muiTheme.palette.textColor : '#434343'

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={size ?? 'lg'}
      onClose={handleClose}
      sx={{
        ...(widthInPx ? { width: widthInPx, margin: '0 auto' } : {}),
        ...externalSx,
      }}
    >
      <DialogTitle sx={{ m: 0, p: 0 }} id="form-dialog-title">
        <Toolbar variant={compact ? 'dense' : 'regular'} sx={{
          color: compact ? 'inherit' : titleColor,
          backgroundColor: compact ? 'transparent' : titleBgColor || 'transparent',
          boxShadow: compact ? 'none' : 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
          ...(compact ? { minHeight: 'unset', py: 1, px: 2 } : {}),
        }}>
          {icon}

          <span style={{ marginLeft: '10px' }}>{title}</span>
          
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon sx={{ mt: .5, pb: .5, pl: .4, pr: .4 }} />
          </IconButton>
        </Toolbar>
      </DialogTitle>

      <DialogContent sx={{ minHeight: minHeight ?? '200px', maxHeight: maxHeight ?? '85vh', overflow: 'auto', padding: '20px', mt: 2 }}>
        {children}
      </DialogContent>
      
      {displayDialogActions && (
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {closeLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export const FullPanelDialog = ({ open, onClose, title, icon, children }) => {
  const [theme] = useTheme()
  const isDark = theme === 'dark'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          borderRadius: '12px',
          backgroundColor: isDark ? '#111' : '#F3F4F6',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* Top bar */}
      <DialogTitle sx={{ m: 0, p: 0 }}>
        <Toolbar
          variant="dense"
          sx={{
            px: 2.5,
            py: 1.5,
            minHeight: 'unset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isDark
              ? 'linear-gradient(90deg, #1a2a2a 0%, #1a1a1a 100%)'
              : 'linear-gradient(90deg, #daeeed 0%, #f0fdf9 40%, #fff 100%)',
            borderBottom: `1px solid ${isDark ? '#333' : '#c2e0df'}`,
            borderRadius: '12px 12px 0 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {icon && (
              <div style={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(45,140,122,0.15)' : 'rgba(45,140,122,0.1)',
                border: `1px solid ${isDark ? '#2D8C7A' : 'rgba(45,140,122,0.25)'}`,
              }}>
                {React.cloneElement(icon, {
                  sx: { fontSize: '18pt', color: isDark ? '#5eaca0' : '#2D8C7A' }
                })}
              </div>
            )}
            <span style={{ fontWeight: 700, color: isDark ? '#ddd' : '#333', fontSize: '1.1rem' }}>
              {title}
            </span>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: isDark ? '#aaa' : '#777',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(220,38,38,0.1)' : 'rgba(220,38,38,0.06)',
                color: '#DC2626',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </DialogTitle>

      {/* Content — no padding, consumer controls layout */}
      {children}
    </Dialog>
  )
}

const CustomDialog = ({ open, handleClose, title, size, style, children }) => (
  <Dialog
    open={open}
    onClose={handleClose}
    fullWidth
    maxWidth={size ?? 'md'}
    sx={{ style }}
  >
    <DialogTitle sx={{ m: 0, p: 2 }}>
      <Toolbar>
        {title}
        <IconButton
          aria-label="close"
          color="inherit"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
    </DialogTitle>

    <DialogContent>
      {children}
    </DialogContent>
  </Dialog>
)

export default CustomDialog
